<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
	ApiError,
	completeAttempt as completeAttemptRequest,
	createAttempt as createAttemptRequest,
	createPlayer,
	getGame,
	getLeaderboard,
	lookupPlayer,
	saveSurvey,
	validateAnswer,
} from './api';
import BrandHero from './components/BrandHero.vue';
import CategorySelector from './components/CategorySelector.vue';
import DynamicForm from './components/DynamicForm.vue';
import QuestionView from './components/QuestionView.vue';
import { translator, type TranslationKey } from './i18n';
import { renderInstructions, renderRichText } from './instructions';
import { fireRegistrationNotification } from './notifications';
import {
	buildEmbeddedPreviewUrl,
	embeddedPreviewReadyType,
	readEmbeddedPreviewUpdate,
	readPreviewOptions,
	type PreviewAnswerState,
	type EmbeddedPreviewUpdate,
} from './preview';
import { shouldRetryGuess } from './questionState';
import { applyTheme } from './theme';
import {
	bingoLines,
	bingoPatternLabel,
	bingoPatternMatches,
	bingoScoreBreakdown,
	formatBingoBonusCopy,
	type BingoScoreBreakdown,
	type BingoScoreRules,
} from './scoring';
import type {
	AnswerRecord,
	Attempt,
	CompletedReward,
	CompletedResult,
	ConnectedForm,
	GameConfig,
	LeaderboardRow,
	Player,
	Question,
	QuestionFeedback,
} from './types';

type Screen =
	| 'loading'
	| 'unavailable'
	| 'login'
	| 'leadCapture'
	| 'confirmation'
	| 'survey'
	| 'instructions'
	| 'category'
	| 'categoryReveal'
	| 'question'
	| 'clashBoard'
	| 'roundPrompt'
	| 'bingoBoard'
	| 'final'
	| 'leaderboard';
type PlayerConfirmation = 'home' | 'endClash' | 'submitBingo';
interface BingoBonusNotice {
	key: string;
	points: number;
	copy: string;
}

const game = ref<GameConfig | null>(null);
const screen = ref<Screen>('loading');
const errorMessage = ref('');
const busy = ref(false);
const showFormErrors = ref(false);
const player = ref<Player | null>(null);
const pendingIdentity = ref<Record<string, unknown> | null>(null);
const attempt = ref<Attempt | null>(null);
const result = ref<CompletedResult | null>(null);
const rewardPopup = ref<CompletedReward | null>(null);
const leaderboard = ref<LeaderboardRow[]>([]);
const identity = ref<Record<string, unknown>>({});
const surveyValues = ref<Record<string, unknown>>({});
const surveyResponseId = ref<number | null>(null);
const nameValue = ref('');
const topLogoFailed = ref(false);
const playerConfirmation = ref<PlayerConfirmation | null>(null);
const confirmationCancelButton = ref<HTMLButtonElement | null>(null);
const confirmationPausedTimer = ref(false);
const pendingBingoBonuses = ref<BingoBonusNotice[]>([]);
const activeBingoBonus = ref<BingoBonusNotice | null>(null);
const bingoBoardRegion = ref<HTMLElement | null>(null);
const bingoBoardSize = ref<number | null>(null);
let bingoBoardObserver: ResizeObserver | null = null;

const answers = ref<AnswerRecord[]>([]);
const score = ref(0);
const streak = ref(0);
const currentQuestion = ref<Question | null>(null);
const currentBoardIndex = ref<number | undefined>();
const selected = ref<string[]>([]);
const rankOrder = ref<string[]>([]);
const guess = ref('');
const guessHistory = ref<string[]>([]);
const guessRetry = ref(false);
const feedback = ref<QuestionFeedback | null>(null);
const quizQuestions = ref<Question[]>([]);
const clashQuestions = ref<Question[]>([]);
const bingoQuestions = ref<Question[]>([]);
const selectedCategory = ref('');
const previewAnswerState = ref<PreviewAnswerState>('default');
const remainingMs = ref(0);
const durationMs = ref(0);
const questionDeadline = ref(0);
const questionTimerRunning = ref(false);
const playStartedAt = ref(0);
let timerId: number | undefined;
let leaderboardPollId: number | undefined;
let playGeneration = 0;

const query = new URLSearchParams(window.location.search);
const previewOptions = readPreviewOptions(window.location.search);
const previewMode = previewOptions.enabled;
const previewFrame = previewMode && !previewOptions.embedded;
const previewIframeUrl = buildEmbeddedPreviewUrl(window.location.href, previewOptions);

const preventPreviewInteraction = (event: Event): void => {
	if (!previewMode) return;
	const target = event.target;
	if (!(target instanceof Element) || !target.closest('button, input, select, textarea, a, label, [role="button"], .letter-boxes')) return;
	event.preventDefault();
	event.stopPropagation();
};
let pendingEmbeddedPreviewUpdate: EmbeddedPreviewUpdate | null = null;

const mergeEmbeddedPreviewUpdates = (
	current: EmbeddedPreviewUpdate | null,
	next: EmbeddedPreviewUpdate,
): EmbeddedPreviewUpdate => ({
	theme: { ...(current?.theme ?? {}), ...(next.theme ?? {}) },
	categorySelectorSettings: {
		...(current?.categorySelectorSettings ?? {}),
		...(next.categorySelectorSettings ?? {}),
	},
	gameLabels: { ...(current?.gameLabels ?? {}), ...(next.gameLabels ?? {}) },
	answerState: next.answerState ?? current?.answerState,
});

const applyEmbeddedPreviewUpdate = (update: EmbeddedPreviewUpdate): void => {
	if (!game.value) {
		pendingEmbeddedPreviewUpdate = mergeEmbeddedPreviewUpdates(pendingEmbeddedPreviewUpdate, update);
		return;
	}
	if (update.theme?.topLogoUrl !== undefined && update.theme.topLogoUrl !== game.value.theme.topLogoUrl) {
		topLogoFailed.value = false;
	}
	if (update.theme) Object.assign(game.value.theme, update.theme);
	if (update.categorySelectorSettings) {
		Object.assign(game.value.settings.categorySelectorSettings, update.categorySelectorSettings);
	}
	if (update.gameLabels) Object.assign(game.value.settings, update.gameLabels);
	if (update.answerState) {
		previewAnswerState.value = update.answerState;
		if (currentQuestion.value) applyPreviewAnswerState(update.answerState);
	}
	applyTheme(game.value.theme);
};

const receiveEmbeddedPreviewUpdate = (event: MessageEvent): void => {
	if (!previewMode || !previewOptions.embedded) return;
	if (event.origin !== window.location.origin || event.source !== window.parent) return;
	const update = readEmbeddedPreviewUpdate(event.data);
	if (update) applyEmbeddedPreviewUpdate(update);
};
const kiosk = computed(() => Boolean(game.value?.settings.kioskMode));
const t = (key: TranslationKey): string => translator(game.value?.settings.language ?? 'en')(key);
const gameType = computed(() => game.value?.settings.gameType ?? 'quiz');
const activePlay = computed(() => ['category', 'categoryReveal', 'question', 'clashBoard', 'roundPrompt', 'bingoBoard'].includes(screen.value));
const headerShowsPoints = computed(() => screen.value === 'question' || screen.value === 'final');
const headerStatusLabel = computed((): string => {
	switch (screen.value) {
		case 'login':
		case 'leadCapture':
		case 'confirmation':
			return t('headerLogin');
		case 'survey':
			return t('headerSurvey');
		case 'instructions':
			return t('headerInstructions');
		case 'category':
			return t('headerCategory');
		case 'categoryReveal':
		case 'roundPrompt':
			return t('headerReveal');
		case 'bingoBoard':
			return t('headerBingo');
		case 'clashBoard':
			return t('headerCategoryClash');
		case 'leaderboard':
			return t('headerLeaderboard');
		default:
			return '';
	}
});
const questionTotal = computed(() => {
	if (!game.value) return 0;
	if (gameType.value === 'categoryClash') return 20;
	if (gameType.value === 'bingo') return 9;
	return game.value.settings.questionCount;
});
const currentQuestionNumber = computed(() => Math.min(
	questionTotal.value,
	answers.value.length + (feedback.value ? 0 : 1),
));
const instructionsHtml = computed(() => renderInstructions(game.value?.settings.instructionsText ?? ''));
const playerDisplayName = computed(() =>
	`${player.value?.firstName ?? ''} ${player.value?.lastName ?? ''}`.trim()
	|| player.value?.fullName
	|| `${pendingIdentity.value?.firstName ?? ''} ${pendingIdentity.value?.lastName ?? ''}`.trim()
	|| String(pendingIdentity.value?.fullName ?? '').trim(),
);
const playerEmailDisplay = computed(() => player.value?.email || String(pendingIdentity.value?.email ?? '').trim());
const clashCategories = computed(() => [...new Set(clashQuestions.value.map((question) => question.category))]);
const usedQuestionIds = computed(() => new Set(answers.value.map((answer) => answer.questionId)));
const selectorTargetQuestion = computed(() => quizQuestions.value[answers.value.length]);
const categoryOption = (category: string) => {
	const name = category || 'General';
	const categories = [...new Set(game.value?.questions.map((question) => question.category || 'General') ?? [])];
	const index = Math.max(0, categories.indexOf(name));
	const palette = game.value?.theme.categoryPalette ?? ['#2458D8'];
	return { name, color: palette[index % palette.length] };
};
const selectorTarget = computed(() => categoryOption(selectorTargetQuestion.value?.category || 'General'));
const selectorCategories = computed(() => {
	const names = new Set<string>();
	return quizQuestions.value
		.slice(answers.value.length)
		.map((question) => question.category || 'General')
		.filter((category) => {
			if (names.has(category)) return false;
			names.add(category);
			return true;
		})
		.map(categoryOption);
});
const selectorLabels = computed(() => ({
	answerQuestion: t('answerQuestion'),
	category: t('category'),
	flipCardHelp: t('flipCardHelp'),
	flipCardTitle: t('flipCardTitle'),
	pullCategoryHelp: t('pullCategoryHelp'),
	pullCategoryTitle: t('pullCategoryTitle'),
	revealQuestion: t('revealQuestion'),
	spin: t('spin'),
	spinCategoryHelp: t('spinCategoryHelp'),
	spinCategoryTitle: t('spinCategoryTitle'),
	tapSpinCategory: t('tapSpinCategory'),
}));
const bingoMarked = computed(() =>
	answers.value.filter((answer) => answer.correct).map((answer) => answer.boardIndex ?? -1).filter((index) => index >= 0),
);
const bingoRules = computed<BingoScoreRules>(() => ({
	patterns: game.value?.settings.scoring.bingoWinningPatterns ?? ['row', 'column', 'diagonal'],
	correctAnswerPoints: game.value?.settings.scoring.bingoCorrectAnswerPoints ?? 10,
	bonusEnabled: game.value?.settings.scoring.bingoBonusEnabled ?? true,
	patternBonusPoints: game.value?.settings.scoring.bingoPatternBonusPoints ?? 50,
	fullBoardBonusEnabled: game.value?.settings.scoring.bingoFullBoardBonusEnabled ?? false,
	fullBoardBonusPoints: game.value?.settings.scoring.bingoFullBoardBonusPoints ?? 100,
	streakEnabled: game.value?.settings.scoring.streakEnabled ?? false,
	streakMultiplierStep: game.value?.settings.scoring.streakMultiplierStep ?? 1,
	useRecordedAnswerAwards: true,
}));
const bingoCompletedPatterns = computed(() => bingoPatternMatches(bingoMarked.value, bingoRules.value.patterns));
const bingoCompletedLines = computed(() => bingoLines(bingoMarked.value, bingoRules.value.patterns));
const bingoScoreDetails = computed(() => bingoScoreBreakdown(answers.value, bingoRules.value));
const bingoCorrectCount = computed(() => answers.value.filter((answer) => answer.correct).length);
const canSubmitBingo = computed(() => bingoQuestions.value.length > 0);
const bingoLineClass = (line: number[]): string => ({
	'0,1,2': 'line-row-1',
	'3,4,5': 'line-row-2',
	'6,7,8': 'line-row-3',
	'0,3,6': 'line-column-1',
	'1,4,7': 'line-column-2',
	'2,5,8': 'line-column-3',
	'0,4,8': 'line-diagonal-1',
	'2,4,6': 'line-diagonal-2',
}[line.join(',')] ?? '');
const updateBingoBoardSize = (): void => {
	const region = bingoBoardRegion.value;
	const mobileLayout = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
	if (!region || !kiosk.value || mobileLayout) {
		bingoBoardSize.value = null;
		return;
	}
	bingoBoardSize.value = Math.max(0, Math.floor(Math.min(region.clientWidth, region.clientHeight)));
};
watch(bingoBoardRegion, (region) => {
	bingoBoardObserver?.disconnect();
	bingoBoardObserver = null;
	if (!region || typeof ResizeObserver === 'undefined') return;
	bingoBoardObserver = new ResizeObserver(updateBingoBoardSize);
	bingoBoardObserver.observe(region);
	updateBingoBoardSize();
});
watch(kiosk, updateBingoBoardSize);
const showNextBingoBonus = (): void => {
	activeBingoBonus.value = pendingBingoBonuses.value.shift() ?? null;
};
const queueBingoBonuses = (before: BingoScoreBreakdown, after: BingoScoreBreakdown): void => {
	if (!bingoRules.value.bonusEnabled) return;
	const previousPatterns = new Set(before.completedPatterns.map((pattern) => pattern.key));
	const newPatterns = after.completedPatterns.filter((pattern) => !previousPatterns.has(pattern.key));
	const fullBoardBonus = Math.max(0, after.fullBoardBonus - before.fullBoardBonus);
	const copyTemplate = game.value?.settings.scoring.bingoBonusCopy
		?? 'You completed the {pattern} for +{points} bonus points.';

	newPatterns.forEach((pattern) => {
		const points = bingoRules.value.patternBonusPoints
			+ (pattern.key === 'fullBoard' ? fullBoardBonus : 0);
		const patternLabel = bingoPatternLabel(pattern.key);
		pendingBingoBonuses.value.push({
			key: pattern.key,
			points,
			copy: formatBingoBonusCopy(copyTemplate, patternLabel, points),
		});
	});

	if (fullBoardBonus > 0 && !newPatterns.some((pattern) => pattern.key === 'fullBoard')) {
		const patternLabel = bingoPatternLabel('fullBoard');
		pendingBingoBonuses.value.push({
			key: 'fullBoardBonus',
			points: fullBoardBonus,
			copy: formatBingoBonusCopy(copyTemplate, patternLabel, fullBoardBonus),
		});
	}
};
const finalScore = computed(() => result.value?.finalScore ?? score.value);
const resultSetClosed = computed(() => Boolean(game.value?.event.active && !game.value.hasActiveResultSet));
const unavailableTitle = computed(() => {
	if (resultSetClosed.value) return game.value?.settings.gameOver.title ?? 'Game Over';
	if (game.value && !game.value.event.active) return game.value.settings.completion.title;
	return t('unavailable');
});
const unavailableDescription = computed(() => {
	if (resultSetClosed.value) return game.value?.settings.gameOver.description ?? '';
	if (game.value && !game.value.event.active) return game.value.gameOverText || game.value.settings.completion.message;
	return '';
});
const unavailableDescriptionHtml = computed(() => renderRichText(unavailableDescription.value));
const canReplay = computed(() => Boolean(
	game.value?.settings.replay.enabled
	&& result.value
	&& (!game.value.settings.replay.limitEnabled || result.value.attemptNumber < game.value.settings.replay.maxPlays),
));
const customCallToActionEnabled = computed(() => Boolean(!kiosk.value && game.value?.settings.completion.primaryUrl));
const showFinalActions = computed(() => customCallToActionEnabled.value || canReplay.value || kiosk.value);
const endGameButtonLabel = computed(() => game.value?.settings.completion.secondaryLabel || t('done'));
const bingoConfirmationText = (key: 'bingoHomeConfirm' | 'submitBingoConfirm'): string =>
	t(key).replace('{score}', String(bingoScoreDetails.value.total));
const playerConfirmationTitle = computed(() => {
	if (playerConfirmation.value === 'home') return gameType.value === 'bingo' ? bingoConfirmationText('bingoHomeConfirm') : t('homeConfirm');
	if (playerConfirmation.value === 'endClash') return t('endConfirm');
	return bingoConfirmationText('submitBingoConfirm');
});
const playerConfirmationCancelLabel = computed(() => playerConfirmation.value === 'home' ? t('keepPlaying') : t('back'));
const playerConfirmationActionLabel = computed(() => {
	if (playerConfirmation.value === 'home') return gameType.value === 'bingo' ? t('returnHome') : t('endGame');
	if (playerConfirmation.value === 'endClash') return game.value?.settings.categoryClashButtonLabel || t('submitScoreEndGame');
	return game.value?.settings.bingoSubmitButtonLabel || t('submitBingo');
});
const streakMultiplier = computed(() => {
	if (!game.value?.settings.scoring.streakEnabled || streak.value < 1) return 1;
	return 1 + Math.max(0, streak.value - 1) * game.value.settings.scoring.streakMultiplierStep;
});
let audioContext: AudioContext | null = null;

const shuffle = <T>(items: T[]): T[] => {
	const result = [...items];
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[result[i], result[j]] = [result[j], result[i]];
	}
	return result;
};

const categoryStyle = (category: string): Record<string, string> => {
	if (!game.value) return {};
	return {
		backgroundColor: categoryOption(category).color,
		color: game.value.theme.categoryLabelText,
	};
};

const playFeedbackSound = (correct: boolean): void => {
	if (!game.value?.settings.playSounds) return;
	try {
		audioContext ??= new AudioContext();
		if (audioContext.state === 'suspended') void audioContext.resume();
		const oscillator = audioContext.createOscillator();
		const gain = audioContext.createGain();
		oscillator.frequency.value = correct ? 660 : 180;
		gain.gain.setValueAtTime(0.08, audioContext.currentTime);
		gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.18);
		oscillator.connect(gain).connect(audioContext.destination);
		oscillator.start();
		oscillator.stop(audioContext.currentTime + 0.18);
	} catch {
		// Sound is optional; browsers may block audio until a user gesture.
	}
};

const formDefaults = (form: ConnectedForm | null): Record<string, unknown> => {
	const values: Record<string, unknown> = {};
	for (const input of form?.inputs ?? []) {
		if (input.type === 'checkbox-optout') values[input.name || `field_${input.id}`] = true;
	}
	return values;
};

const routeSlugs = (): [string, string] => {
	const parts = window.location.pathname.split('/').filter(Boolean);
	const index = parts.indexOf('trivia');
	const eventSlug = query.get('event') || (index >= 0 ? parts[index + 1] : '') || '';
	const gameSlug = query.get('game') || (index >= 0 ? parts[index + 2] : '') || '';
	return [eventSlug, gameSlug];
};

const readPreviewSessionUpdate = (): EmbeddedPreviewUpdate | null => {
	if (!previewMode || !previewOptions.previewSession) return null;
	const storageKey = `trivia-preview:${previewOptions.previewSession}`;
	const previousStorageKey = `${['new', 'trivia-preview'].join('-')}:${previewOptions.previewSession}`;
	try {
		const stored = window.localStorage.getItem(storageKey) || window.localStorage.getItem(previousStorageKey);
		if (!stored) return null;
		const snapshot = JSON.parse(stored) as { expiresAt?: unknown; message?: unknown };
		if (typeof snapshot.expiresAt !== 'number' || snapshot.expiresAt < Date.now()) {
			window.localStorage.removeItem(storageKey);
			window.localStorage.removeItem(previousStorageKey);
			return null;
		}
		return readEmbeddedPreviewUpdate(snapshot.message);
	} catch {
		window.localStorage.removeItem(storageKey);
		window.localStorage.removeItem(previousStorageKey);
		return null;
	}
};

const load = async (): Promise<void> => {
	screen.value = 'loading';
	errorMessage.value = '';
	try {
		const [eventSlug, gameSlug] = routeSlugs();
		if (!eventSlug || !gameSlug || eventSlug === 'dist' || gameSlug === 'dist') {
			throw new Error('The trivia player link is incomplete.');
		}
		game.value = await getGame(eventSlug, gameSlug);
		const previewSessionUpdate = readPreviewSessionUpdate();
		if (previewSessionUpdate) {
			pendingEmbeddedPreviewUpdate = mergeEmbeddedPreviewUpdates(pendingEmbeddedPreviewUpdate, previewSessionUpdate);
		}
		if (pendingEmbeddedPreviewUpdate) {
			const update = pendingEmbeddedPreviewUpdate;
			pendingEmbeddedPreviewUpdate = null;
			applyEmbeddedPreviewUpdate(update);
		}
		topLogoFailed.value = false;
		identity.value = formDefaults(game.value.leadCaptureForm);
		surveyValues.value = formDefaults(game.value.surveyForm);
		document.documentElement.lang = game.value.settings.language;
		document.title = `${game.value.title || game.value.name} · Trivia`;
		applyTheme(game.value.theme);
		if (previewMode) {
			initializePreview();
			return;
		}

		if (query.get('leaderboard') === '1') {
			await loadLeaderboard();
			screen.value = 'leaderboard';
			leaderboardPollId = window.setInterval(() => {
				void loadLeaderboard();
			}, 2000);
			return;
		}
		if (!game.value.playable) {
			screen.value = 'unavailable';
			return;
		}
		resetToLogin();
	} catch (error) {
		errorMessage.value = error instanceof Error ? error.message : 'The game could not be loaded.';
		screen.value = 'unavailable';
	}
};

const resetToLogin = (): void => {
	stopTimer();
	playerConfirmation.value = null;
	confirmationPausedTimer.value = false;
	player.value = null;
	pendingIdentity.value = null;
	attempt.value = null;
	result.value = null;
	rewardPopup.value = null;
	identity.value = formDefaults(game.value?.leadCaptureForm ?? null);
	surveyValues.value = formDefaults(game.value?.surveyForm ?? null);
	surveyResponseId.value = null;
	nameValue.value = '';
	showFormErrors.value = false;
	resetPlayState();
	screen.value = game.value?.settings.loginMode === 'leadCapture' ? 'leadCapture' : 'login';
};

const resetPlayState = (): void => {
	playGeneration++;
	stopTimer();
	answers.value = [];
	score.value = 0;
	streak.value = 0;
	currentQuestion.value = null;
	currentBoardIndex.value = undefined;
	selected.value = [];
	rankOrder.value = [];
	guess.value = '';
	guessHistory.value = [];
	guessRetry.value = false;
	feedback.value = null;
	quizQuestions.value = [];
	clashQuestions.value = [];
	bingoQuestions.value = [];
	pendingBingoBonuses.value = [];
	activeBingoBonus.value = null;
	selectedCategory.value = '';
	remainingMs.value = 0;
	durationMs.value = 0;
	questionDeadline.value = 0;
	playStartedAt.value = 0;
	errorMessage.value = '';
};

const samplePreviewQuestion = (id: number, category: string, points: number): Question => ({
	id,
	type: 'mc4',
	category,
	points,
	ordinal: id,
	text: id % 2 ? 'Which planet is known as the Red Planet?' : 'Which ocean is the largest on Earth?',
	hint: '',
	explanation: '',
	imageUrl: '',
	videoUrl: '',
	videoRevealDurationSeconds: 0,
	answerLength: 0,
	options: [
		{ id: 'a', text: id % 2 ? 'Mars' : 'Pacific Ocean', imageUrl: '' },
		{ id: 'b', text: id % 2 ? 'Venus' : 'Atlantic Ocean', imageUrl: '' },
		{ id: 'c', text: id % 2 ? 'Jupiter' : 'Indian Ocean', imageUrl: '' },
		{ id: 'd', text: id % 2 ? 'Mercury' : 'Arctic Ocean', imageUrl: '' },
	],
});

const samplePreviewQuestions = (): Question[] =>
	['History', 'Music', 'Sports', 'Science'].flatMap((category, categoryIndex) =>
		[100, 200, 300, 400, 500].map((points, questionIndex) =>
			samplePreviewQuestion(-(categoryIndex * 5 + questionIndex + 1), category, points)),
	);

const applyPreviewAnswerState = (state: PreviewAnswerState): void => {
	const question = currentQuestion.value;
	if (!previewMode || !question) return;
	previewAnswerState.value = state;
	busy.value = state === 'disabled';
	selected.value = [];
	feedback.value = null;
	guess.value = '';
	guessHistory.value = [];
	guessRetry.value = false;
	rankOrder.value = question.options.map((option) => option.id);
	if (state === 'default' || state === 'disabled') return;

	const firstId = question.options[0]?.id ?? (question.type === 'tf' ? 't' : 'a');
	const secondId = question.options[1]?.id ?? (question.type === 'tf' ? 'f' : firstId);
	if (question.type === 'gw') guess.value = state === 'incorrect' ? 'WRONG' : 'ANSWER';
	else if (question.type !== 'ro') selected.value = [state === 'incorrect' ? secondId : firstId];
	if (state === 'selected') return;

	const correct = state === 'correct';
	const rankingAnswer = question.options
		.map((option) => option.id)
		.reverse()
		.join(',');
	feedback.value = {
		questionId: question.id,
		correct,
		correctResponse: question.type === 'gw' ? null : (question.type === 'ro' ? rankingAnswer : firstId),
		explanation: question.explanation,
		award: correct ? question.points : 0,
		timedOut: false,
	};
};

const clashPreviewQuestions = (configured: Question[], samples: Question[]): Question[] => {
	const categories = [...new Set(configured.map((question) => question.category).filter(Boolean))].slice(0, 4);
	for (const sample of samples) {
		if (categories.length >= 4) break;
		if (!categories.includes(sample.category)) categories.push(sample.category);
	}
	return categories.flatMap((category, categoryIndex) =>
		[100, 200, 300, 400, 500].map((points, pointIndex) => {
			const configuredQuestion = configured.find((question) => question.category === category && question.points === points);
			if (configuredQuestion) return configuredQuestion;
			return samplePreviewQuestion(-(100 + categoryIndex * 5 + pointIndex), category, points);
		}),
	);
};

const initializePreviewResults = (): void => {
	if (!game.value) return;
	const previewTotal = Math.max(1, questionTotal.value);
	const correctAnswers = Math.min(8, previewTotal);
	const completedAt = new Date().toISOString();
	player.value = { id: -1, fullName: 'You', firstName: '', lastName: '', email: '' };
	score.value = 1200;
	result.value = {
		id: -1,
		attemptId: -1,
		gameId: game.value.id,
		playerId: -1,
		gameType: gameType.value,
		attemptNumber: 1,
		finalScore: 1200,
		correctAnswers,
		questionsAnswered: previewTotal,
		completionTimeMs: 54200,
		completionReason: 'completed',
		completedAt,
		reward: null,
	};
	leaderboard.value = [
		{ rank: 1, playerId: -2, name: 'Bonnie', score: 1800, correctAnswers: Math.min(previewTotal, correctAnswers + 1), questionsAnswered: previewTotal, completionTimeMs: 48100, completedAt },
		{ rank: 2, playerId: -1, name: 'You', score: 1200, correctAnswers, questionsAnswered: previewTotal, completionTimeMs: 54200, completedAt },
		{ rank: 3, playerId: -3, name: 'Andy', score: 950, correctAnswers: Math.max(0, correctAnswers - 1), questionsAnswered: previewTotal, completionTimeMs: 61700, completedAt },
	];
};

const initializePreview = (): void => {
	if (!game.value) return;
	resetPlayState();
	const canonical = [...game.value.questions].sort((a, b) => a.ordinal - b.ordinal);
	const samples = samplePreviewQuestions();

	if (previewOptions.screen === 'global') {
		resetToLogin();
		screen.value = 'login';
		return;
	}

	if (previewOptions.screen === 'login') {
		resetToLogin();
		return;
	}

	if (previewOptions.screen === 'question') {
		const configuredPreview = canonical.find((question) => ['mc4', 'vmc4'].includes(question.type) && question.options.length >= 4);
		const sample = samplePreviewQuestion(-1, canonical[0]?.category || 'Science', canonical[0]?.points || 100);
		currentQuestion.value = configuredPreview ?? {
			...sample,
			text: canonical[0]?.text || sample.text,
			hint: canonical[0]?.hint || sample.hint,
			imageUrl: canonical[0]?.imageUrl || sample.imageUrl,
		};
		rankOrder.value = currentQuestion.value.options.map((option) => option.id);
		if (game.value.settings.scoring.timerEnabled) {
			durationMs.value = game.value.settings.scoring.durationSeconds * 1000;
			remainingMs.value = durationMs.value;
		}
		screen.value = 'question';
		applyPreviewAnswerState(previewAnswerState.value);
		return;
	}

	if (previewOptions.screen === 'category') {
		if (gameType.value === 'quiz') {
			const previewCount = Math.max(1, game.value.settings.questionCount);
			quizQuestions.value = [...canonical.slice(0, previewCount), ...samples].slice(0, previewCount);
			screen.value = 'category';
			return;
		}
		if (gameType.value === 'categoryClash') {
			clashQuestions.value = clashPreviewQuestions(canonical, samples);
			screen.value = 'clashBoard';
			return;
		}
		bingoQuestions.value = [...canonical.slice(0, 9), ...samples].slice(0, 9);
		screen.value = 'bingoBoard';
		return;
	}

	initializePreviewResults();
	screen.value = previewOptions.screen === 'leaderboard' ? 'leaderboard' : 'final';
};

const fieldValue = (patterns: RegExp[]): string => {
	if (!game.value?.leadCaptureForm) return '';
	for (const input of game.value.leadCaptureForm.inputs) {
		const key = input.name || `field_${input.id}`;
		const searchable = `${input.type} ${input.name} ${input.label}`.toLowerCase();
		if (patterns.some((pattern) => pattern.test(searchable))) return String(identity.value[key] ?? '').trim();
	}
	return '';
};

const formValid = (values: Record<string, unknown>, inputs = game.value?.leadCaptureForm?.inputs ?? []): boolean =>
	inputs.every((input) => {
		const value = values[input.name || `field_${input.id}`];
		if (input.type === 'email' && value != null && String(value).trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
			return false;
		}
		if (!input.isRequired) return true;
		if (input.type === 'multi-select' && input.minimumSelections > 0) {
			return Array.isArray(value) && value.length >= input.minimumSelections;
		}
		if (['checkbox', 'checkbox-optout', 'terms', 'consent'].includes(input.type)) return Boolean(value);
		if (typeof value === 'string') return value.trim().length > 0;
		return value != null && (!Array.isArray(value) || value.length > 0);
	});

const submitIdentity = async (): Promise<void> => {
	if (previewMode || !game.value) return;
	showFormErrors.value = true;
	errorMessage.value = '';
	let payload: Record<string, unknown>;

	if (game.value.settings.loginMode === 'leadCapture') {
		if (!game.value.leadCaptureForm || !formValid(identity.value)) return;
		const firstName = fieldValue([/\bfirst\b/, /firstname/]);
		const lastName = fieldValue([/\blast\b/, /lastname/]);
		const email = fieldValue([/email/]);
		const fullName = firstName || lastName ? '' : fieldValue([/full.?name/, /\bname\b/]);
		payload = { firstName, lastName, email, fullName, responses: identity.value };
	} else {
		if (!nameValue.value.trim()) return;
		payload = { fullName: nameValue.value.trim() };
	}

	showFormErrors.value = false;
	if (game.value.settings.loginMode === 'leadCapture' && game.value.notifyUrl) {
		const generation = playGeneration;
		const gameId = game.value.id;
		busy.value = true;
		try {
			const result = await lookupPlayer(gameId, payload);
			if (generation !== playGeneration || game.value?.id !== gameId) return;
			if (!result.registered) fireRegistrationNotification(game.value.notifyUrl, payload);
		} catch (error) {
			if (generation === playGeneration) showApiError(error);
			return;
		} finally {
			busy.value = false;
		}
	}
	if (game.value.settings.showConfirmation) {
		pendingIdentity.value = payload;
		screen.value = 'confirmation';
		return;
	}
	if (await persistIdentity(payload)) advanceAfterIdentity();
};

const persistIdentity = async (payload: Record<string, unknown>): Promise<boolean> => {
	if (previewMode || !game.value) return false;
	const generation = playGeneration;
	const gameId = game.value.id;
	busy.value = true;
	errorMessage.value = '';
	try {
		const registrationPayload = { ...payload };
		if (game.value.settings.loginMode === 'leadCapture' && game.value.notifyEmail) {
			registrationPayload.notifyEmail = game.value.notifyEmail;
			registrationPayload.gameName = game.value.name;
		}
		const createdPlayer = await createPlayer(gameId, registrationPayload);
		if (generation !== playGeneration || game.value?.id !== gameId) return false;
		player.value = createdPlayer;
		pendingIdentity.value = null;
		showFormErrors.value = false;
		return true;
	} catch (error) {
		if (generation === playGeneration) showApiError(error);
		return false;
	} finally {
		busy.value = false;
	}
};

const confirmIdentity = async (): Promise<void> => {
	if (!pendingIdentity.value) return;
	const payload = pendingIdentity.value;
	if (await persistIdentity(payload)) advanceAfterIdentity();
};

const advanceAfterIdentity = (): void => {
	if (game.value?.settings.showSurvey && game.value.surveyForm) {
		screen.value = 'survey';
		return;
	}
	advanceAfterSurvey();
};

const advanceAfterSurvey = (): void => {
	if (game.value?.settings.showInstructionsScreen) screen.value = 'instructions';
	else void startGame();
};

const editIdentity = (): void => {
	errorMessage.value = '';
	screen.value = game.value?.settings.loginMode === 'leadCapture' ? 'leadCapture' : 'login';
};

const submitSurvey = async (): Promise<void> => {
	if (previewMode || !game.value || !player.value || !game.value.surveyForm) return;
	const generation = playGeneration;
	const gameId = game.value.id;
	const playerId = player.value.id;
	showFormErrors.value = true;
	if (!formValid(surveyValues.value, game.value.surveyForm.inputs)) return;
	busy.value = true;
	errorMessage.value = '';
	try {
		const response = await saveSurvey(gameId, playerId, surveyValues.value);
		if (generation !== playGeneration || player.value?.id !== playerId) return;
		surveyResponseId.value = response.id;
		showFormErrors.value = false;
		advanceAfterSurvey();
	} catch (error) {
		if (generation === playGeneration) showApiError(error);
	} finally {
		busy.value = false;
	}
};

const startGame = async (): Promise<void> => {
	if (previewMode || !game.value || !player.value) return;
	busy.value = true;
	errorMessage.value = '';
	resetPlayState();
	const generation = playGeneration;
	const playerId = player.value.id;
	try {
		const createdAttempt = await createAttemptRequest(game.value.id, playerId, surveyResponseId.value);
		if (generation !== playGeneration || player.value?.id !== playerId) return;
		attempt.value = createdAttempt;
		playStartedAt.value = Date.now();
		prepareGame();
	} catch (error) {
		if (generation === playGeneration) showApiError(error);
	} finally {
		busy.value = false;
	}
};

const prepareGame = (): void => {
	if (!game.value) return;
	const canonical = [...game.value.questions].sort((a, b) => a.ordinal - b.ordinal);
	const questionById = new Map(canonical.map((question) => [question.id, question]));
	const attemptQuestions = (attempt.value?.questions ?? [])
		.map((selection) => {
			const question = selection.question ?? questionById.get(selection.questionId);
			return question ? { ...question, points: selection.points } : null;
		})
		.filter((question): question is Question => question !== null);
	const ordered = game.value.settings.shuffleQuestions
		? shuffle(canonical)
		: canonical;

	if (gameType.value === 'quiz') {
		quizQuestions.value = ordered.slice(0, game.value.settings.questionCount);
		if (game.value.settings.showCategories) {
			screen.value = 'category';
		} else {
			openQuestion(quizQuestions.value[0]);
		}
		return;
	}

	if (gameType.value === 'categoryClash') {
		if( attemptQuestions.length ) clashQuestions.value = attemptQuestions;
		else {
			const categories = [...new Set(canonical.map((question) => question.category))];
			clashQuestions.value = categories.flatMap((category) =>
				canonical
					.filter((question) => question.category === category)
					.sort((a, b) => a.points - b.points)
					.slice(0, 5),
			);
		}
		screen.value = 'clashBoard';
		return;
	}

	bingoQuestions.value = attemptQuestions.length ? attemptQuestions : canonical.slice(0, 9);
	screen.value = 'bingoBoard';
};

const showCategoryReveal = (category: string): void => {
	if (!selectorTargetQuestion.value || category !== selectorTarget.value.name) return;
	selectedCategory.value = selectorTarget.value.name;
	screen.value = 'categoryReveal';
};

const openSelectedCategoryQuestion = (): void => {
	const question = selectorTargetQuestion.value;
	if (!question || (question.category || 'General') !== selectedCategory.value) return;
	openQuestion(question);
};

const openClashQuestion = (question?: Question): void => {
	if (!question || usedQuestionIds.value.has(question.id)) return;
	openQuestion(question);
};

const openBingoQuestion = (question: Question, index: number): void => {
	if (usedQuestionIds.value.has(question.id)) return;
	openQuestion(question, index);
};

const openQuestion = (question?: Question, boardIndex?: number): void => {
	if (!question) return;
	stopTimer();
	currentQuestion.value = question;
	currentBoardIndex.value = boardIndex;
	selected.value = [];
	guess.value = '';
	guessHistory.value = [];
	guessRetry.value = false;
	feedback.value = null;
	rankOrder.value = question.type === 'ro' ? shuffle(question.options.map((option) => option.id)) : [];
	screen.value = 'question';
	nextTick(() => {
		if (question.type !== 'vmc4') beginQuestionTimer();
	});
};

const beginQuestionTimer = (): void => {
	if (!game.value || !game.value.settings.scoring.timerEnabled) {
		durationMs.value = 0;
		remainingMs.value = 0;
		return;
	}
	stopTimer();
	durationMs.value = game.value.settings.scoring.durationSeconds * 1000;
	remainingMs.value = durationMs.value;
	resumeQuestionTimer();
};

const resumeQuestionTimer = (): void => {
	if (remainingMs.value <= 0) {
		stopTimer();
		return;
	}
	stopTimer();
	questionDeadline.value = Date.now() + remainingMs.value;
	questionTimerRunning.value = true;
	timerId = window.setInterval(() => {
		remainingMs.value = Math.max(0, questionDeadline.value - Date.now());
		if (remainingMs.value <= 0) {
			stopTimer();
			void submitAnswer(true);
		}
	}, 100);
};

const stopTimer = (): void => {
	window.clearInterval(timerId);
	timerId = undefined;
	questionTimerRunning.value = false;
};

const responseValue = (): string | string[] | null => {
	if (!currentQuestion.value) return null;
	if (currentQuestion.value.type === 'gw') return guess.value.trim();
	if (currentQuestion.value.type === 'ro') return [...rankOrder.value];
	if (currentQuestion.value.type === 'ms') return [...selected.value].sort();
	return selected.value[0] ?? null;
};

const submitAnswer = async (timedOut = false): Promise<void> => {
	if (previewMode || !game.value || !attempt.value || !currentQuestion.value || busy.value) return;
	const generation = playGeneration;
	const attemptId = attempt.value.id;
	const question = currentQuestion.value;
	const previousBingoScore = gameType.value === 'bingo' ? bingoScoreDetails.value : null;
	if (game.value.settings.playSounds && !audioContext) {
		try {
			audioContext = new AudioContext();
		} catch {
			audioContext = null;
		}
	}
	const response = timedOut ? null : responseValue();
	if (!timedOut && response == null) return;
	stopTimer();
	busy.value = true;
	errorMessage.value = '';

	const nextGuessHistory = question.type === 'gw' && response
		? [...guessHistory.value, String(response)]
		: [...guessHistory.value];
	const guessNumber = question.type === 'gw' && timedOut ? 3 : (nextGuessHistory.length || 1);

	try {
		const validation = await validateAnswer(attemptId, {
			questionId: question.id,
			response,
			remainingMs: remainingMs.value,
			streak: streak.value,
			guessNumber,
			guessHistory: nextGuessHistory,
		});
		if (generation !== playGeneration || attempt.value?.id !== attemptId || currentQuestion.value?.id !== question.id) return;
		playFeedbackSound(validation.correct);

		if (question.type === 'gw' && shouldRetryGuess(timedOut, validation.correct, guessNumber)) {
			guessHistory.value = nextGuessHistory;
			guess.value = '';
			guessRetry.value = true;
			if (durationMs.value > 0) resumeQuestionTimer();
			return;
		}

		let award = validation.award;
		if (gameType.value === 'categoryClash') {
			award = validation.correct ? validation.award : 0;
		} else if (gameType.value === 'bingo') {
			award = validation.correct ? validation.award : 0;
		}

		const record: AnswerRecord = {
			questionId: question.id,
			boardIndex: currentBoardIndex.value,
			response,
			guessHistory: nextGuessHistory.length ? nextGuessHistory : undefined,
			remainingMs: remainingMs.value,
			correct: validation.correct,
			correctResponse: validation.correctResponse,
			award,
		};
		answers.value.push(record);

		if (validation.score !== undefined && validation.score !== null) {
			score.value = validation.score;
		} else if (gameType.value === 'quiz') {
			score.value += award;
		} else if (gameType.value === 'categoryClash') {
			score.value = Math.max(0, score.value + award);
		} else {
			score.value = bingoScoreDetails.value.total;
		}
		if (gameType.value === 'bingo' && previousBingoScore) {
			queueBingoBonuses(previousBingoScore, bingoScoreDetails.value);
		}
		streak.value = validation.correct ? streak.value + 1 : 0;

		if (question.type === 'gw') guessHistory.value = nextGuessHistory;
		guessRetry.value = false;
		feedback.value = { ...validation, award, timedOut };
	} catch (error) {
		if (generation === playGeneration) {
			showApiError(error);
			if (durationMs.value > 0 && remainingMs.value > 0) resumeQuestionTimer();
		}
	} finally {
		busy.value = false;
	}
};

const continueAfterAnswer = (): void => {
	if (!game.value) return;
	if (gameType.value === 'quiz') {
		if (answers.value.length >= game.value.settings.questionCount) {
			void finishGame('completed');
		} else if (game.value.settings.showCategories) {
			selectedCategory.value = '';
			screen.value = 'category';
		} else {
			openQuestion(quizQuestions.value[answers.value.length]);
		}
		return;
	}

	if (gameType.value === 'categoryClash') {
		if (answers.value.length >= 20) {
			void finishGame('completed');
		} else if (answers.value.length % 5 === 0) {
			screen.value = 'roundPrompt';
		} else {
			screen.value = 'clashBoard';
		}
		return;
	}

	screen.value = 'bingoBoard';
	showNextBingoBonus();
};

const openPlayerConfirmation = (confirmation: PlayerConfirmation): void => {
	confirmationPausedTimer.value = timerId !== undefined;
	if (confirmationPausedTimer.value) {
		remainingMs.value = Math.max(0, questionDeadline.value - Date.now());
		stopTimer();
	}
	playerConfirmation.value = confirmation;
	void nextTick(() => confirmationCancelButton.value?.focus());
};

const closePlayerConfirmation = (): void => {
	const resumeTimer = confirmationPausedTimer.value;
	playerConfirmation.value = null;
	confirmationPausedTimer.value = false;
	if (resumeTimer && screen.value === 'question' && remainingMs.value > 0) resumeQuestionTimer();
};

const finishClashEarly = (): void => {
	openPlayerConfirmation('endClash');
};

const submitBingoBoard = (): void => {
	if (!canSubmitBingo.value) return;
	openPlayerConfirmation('submitBingo');
};

const finishGame = async (reason: 'completed' | 'early_end' | 'bingo_submit'): Promise<boolean> => {
	if (previewMode || !attempt.value || busy.value) return false;
	const generation = playGeneration;
	const attemptId = attempt.value.id;
	stopTimer();
	busy.value = true;
	errorMessage.value = '';
	try {
		const completedResult = await completeAttemptRequest(attemptId, {
			completionReason: reason,
			completionTimeMs: Date.now() - playStartedAt.value,
		});
		if (generation !== playGeneration || attempt.value?.id !== attemptId) return false;
		result.value = completedResult;
		score.value = completedResult.finalScore;
		if (game.value?.settings.completion.showLeaderboard) await loadLeaderboard(generation);
		if (generation !== playGeneration || attempt.value?.id !== attemptId) return false;
		screen.value = 'final';
		if (completedResult.reward?.popup.enabled) rewardPopup.value = completedResult.reward;
		return true;
	} catch (error) {
		if (generation === playGeneration) showApiError(error);
		return false;
	} finally {
		busy.value = false;
	}
};

const loadLeaderboard = async (expectedGeneration?: number): Promise<void> => {
	if (!game.value) return;
	try {
		const rows = await getLeaderboard(game.value.id);
		if (expectedGeneration !== undefined && expectedGeneration !== playGeneration) return;
		leaderboard.value = rows;
	} catch (error) {
		if (expectedGeneration === undefined || expectedGeneration === playGeneration) showApiError(error);
	}
};

const primaryAction = (): void => {
	const url = game.value?.settings.completion.primaryUrl;
	if (url) window.location.assign(url);
};

const done = (): void => {
	resetToLogin();
};

const confirmPlayerAction = async (): Promise<void> => {
	const confirmation = playerConfirmation.value;
	if (confirmation === 'home') {
		playerConfirmation.value = null;
		confirmationPausedTimer.value = false;
		resetToLogin();
		return;
	}
	if (confirmation !== 'endClash' && confirmation !== 'submitBingo') return;
	confirmationPausedTimer.value = false;
	const completed = await finishGame(confirmation === 'endClash' ? 'early_end' : 'bingo_submit');
	if (completed) playerConfirmation.value = null;
};

const goHome = (): void => {
	if (questionTimerRunning.value) return;
	if (activePlay.value) {
		openPlayerConfirmation('home');
		return;
	}
	resetToLogin();
};

const playAgain = (): void => {
	if (!player.value) {
		resetToLogin();
		return;
	}
	result.value = null;
	rewardPopup.value = null;
	advanceAfterIdentity();
};

const showApiError = (error: unknown): void => {
	if (error instanceof ApiError && error.code === 'PLAY_LIMIT_REACHED') {
		const message = error.message;
		resetToLogin();
		errorMessage.value = message;
		return;
	}
	if (error instanceof ApiError) errorMessage.value = error.message;
	else if (error instanceof Error) errorMessage.value = error.message;
	else errorMessage.value = 'The request could not be completed.';
};

onMounted(() => {
	if (previewFrame) {
		document.title = 'Trivia Full Preview';
		return;
	}
	if (previewMode && previewOptions.embedded) window.addEventListener('message', receiveEmbeddedPreviewUpdate);
	void load().then(() => {
		if (previewMode && previewOptions.embedded) {
			window.parent.postMessage({ type: embeddedPreviewReadyType }, window.location.origin);
		}
	});
});
onBeforeUnmount(() => {
	stopTimer();
	window.clearInterval(leaderboardPollId);
	bingoBoardObserver?.disconnect();
	window.removeEventListener('message', receiveEmbeddedPreviewUpdate);
	void audioContext?.close();
});
</script>

<template>
	<div v-if="previewFrame" class="new-trivia-preview-host">
		<iframe
			class="new-trivia-preview-iframe"
			:class="`viewport-${previewOptions.viewport}`"
			:src="previewIframeUrl"
			title="Trivia player preview"
			tabindex="-1"
		/>
	</div>
	<div v-else id="new-trivia-app-root" class="player-panel" :class="{ 'is-read-only-preview': previewMode, 'is-kiosk': kiosk, 'is-bingo-screen': screen === 'bingoBoard' }" @click.capture="preventPreviewInteraction">
		<header v-if="game" class="app-header">
			<div>
				<img
					v-if="game.theme.topLogoUrl && !topLogoFailed"
					class="brand-logo"
					:src="game.theme.topLogoUrl"
					:alt="`${game.title || game.name} logo`"
					@error="topLogoFailed = true"
				>
				<div v-else class="game-brand-title">{{ game.title || game.name }}</div>
			</div>
			<div class="header-actions">
				<div v-if="headerStatusLabel" class="header-status">{{ headerStatusLabel }}</div>
				<div v-if="headerShowsPoints" class="score-pill">{{ t('points') }}: {{ finalScore }}</div>
				<button v-if="kiosk && player" type="button" class="home-action" :style="{ backgroundColor: game.settings.buttonHomeBackgroundColor, color: game.settings.buttonHomeIconColor }" :aria-label="t('home')" :title="t('home')" :disabled="questionTimerRunning" @click="goHome">
					<span class="fa" :class="game.settings.buttonHomeIcon" aria-hidden="true" />
					<span class="visually-hidden">{{ t('home') }}</span>
				</button>
			</div>
		</header>

		<main class="app-main" :tabindex="previewMode ? 0 : undefined">
			<section v-if="screen === 'loading'" class="screen-shell screen-narrow">
				<article class="player-card hero-card">
					<span class="loading-spinner" aria-hidden="true" />
					<p>{{ t('loading') }}</p>
				</article>
			</section>

			<section v-else-if="screen === 'unavailable'" class="screen-shell screen-narrow">
				<article class="player-card hero-card">
					<h1>{{ unavailableTitle }}</h1>
					<div v-if="unavailableDescription" class="rich-instructions game-over-rich-text" v-html="unavailableDescriptionHtml" />
					<p v-else-if="errorMessage" class="error-text" role="alert">{{ errorMessage }}</p>
					<ul v-if="game?.event.active && game.hasActiveResultSet && game.validationErrors.length" class="validation-list">
						<li v-for="validationError in game.validationErrors" :key="validationError">{{ validationError }}</li>
					</ul>
				</article>
			</section>

			<section v-else-if="screen === 'login'" class="screen-shell screen-narrow branded-screen">
				<BrandHero
					:logo-url="game?.theme.gameLogoUrl || ''"
					:title="game?.title || game?.name || ''"
					:description="game?.description || ''"
				/>
				<article class="player-card hero-card login-card">
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<div class="field-group">
						<label class="field-label" for="player-name">{{ game?.settings.playerNameLabel || t('name') }}</label>
						<input id="player-name" v-model="nameValue" type="text" maxlength="100" autocomplete="name" @keyup.enter="submitIdentity">
						<p v-if="showFormErrors && !nameValue.trim()" class="field-error">{{ t('required') }}</p>
					</div>
					<div v-if="previewMode && previewOptions.screen === 'global'" class="action-row">
						<button type="button" class="primary-action">Primary Button</button>
						<button type="button" class="secondary-action">Secondary Button</button>
					</div>
					<button v-else type="button" class="primary-action wide-action" :disabled="busy" @click="submitIdentity">{{ t('continue') }}</button>
					<p v-if="game?.settings.footerEnabled && (game.settings.footerText || game.settings.footerLinkLabel)" class="app-footer">
						<span>{{ game.settings.footerText }}</span>
						<a v-if="game.settings.footerLinkLabel && game.settings.footerUrl" :href="game.settings.footerUrl" target="_blank" rel="noopener">{{ game.settings.footerLinkLabel }}</a>
					</p>
				</article>
			</section>

			<section v-else-if="screen === 'leadCapture' && game?.leadCaptureForm" class="screen-shell screen-narrow branded-screen">
				<BrandHero
					:logo-url="game.theme.gameLogoUrl"
					:title="game.title || game.name"
					:description="game.description"
				/>
				<article class="player-card login-card">
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<DynamicForm v-model="identity" :form="game.leadCaptureForm" :show-errors="showFormErrors" :t="t" />
					<button type="button" class="primary-action wide-action" :disabled="busy" @click="submitIdentity">{{ t('continue') }}</button>
					<p v-if="game.settings.footerEnabled && (game.settings.footerText || game.settings.footerLinkLabel)" class="app-footer">
						<span>{{ game.settings.footerText }}</span>
						<a v-if="game.settings.footerLinkLabel && game.settings.footerUrl" :href="game.settings.footerUrl" target="_blank" rel="noopener">{{ game.settings.footerLinkLabel }}</a>
					</p>
				</article>
			</section>

			<section v-else-if="screen === 'confirmation'" class="screen-shell screen-narrow">
				<article class="player-card hero-card">
					<h1>{{ t('confirmTitle') }}</h1>
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<dl class="confirmation-list">
						<dt>{{ t('confirmName') }}</dt><dd>{{ playerDisplayName || '—' }}</dd>
						<dt>{{ t('email') }}</dt><dd>{{ playerEmailDisplay || '—' }}</dd>
					</dl>
					<div class="action-row confirmation-actions">
						<button type="button" class="secondary-action" @click="editIdentity">{{ t('edit') }}</button>
						<button type="button" class="primary-action" :disabled="busy" @click="confirmIdentity">{{ t('continue') }}</button>
					</div>
				</article>
			</section>

			<section v-else-if="screen === 'survey' && game?.surveyForm" class="screen-shell screen-narrow branded-screen">
				<BrandHero
					:logo-url="game.theme.gameLogoUrl"
					:title="game.title || game.name"
					:description="game.description"
				/>
				<article class="player-card">
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<DynamicForm v-model="surveyValues" :form="game.surveyForm" :show-errors="showFormErrors" :show-heading="false" :t="t" />
					<button type="button" class="primary-action wide-action" :disabled="busy" @click="submitSurvey">{{ game.settings.surveyButtonLabel || t('continue') }}</button>
				</article>
			</section>

			<section v-else-if="screen === 'instructions'" class="screen-shell screen-narrow branded-screen">
				<BrandHero
					:logo-url="game?.theme.gameLogoUrl || ''"
					:title="game?.title || game?.name || ''"
					:description="game?.description || ''"
				/>
				<article class="player-card hero-card">
					<h1>{{ game?.settings.instructionsTitle }}</h1>
					<div class="muted rich-instructions" v-html="instructionsHtml" />
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<button type="button" class="primary-action wide-action" :disabled="busy" @click="startGame">{{ game?.settings.startButtonLabel || t('start') }}</button>
				</article>
			</section>

			<section v-else-if="screen === 'category'" class="screen-shell">
				<article v-if="game && selectorTargetQuestion" class="player-card category-selector-card">
					<CategorySelector
						:mode="game.settings.categorySelector"
						:target="selectorTarget"
						:categories="selectorCategories"
						:settings="game.settings.categorySelectorSettings"
						:theme="game.theme"
						:logo-url="game.theme.gameLogoUrl || game.theme.topLogoUrl"
						:game-title="game.title || game.name"
						:labels="selectorLabels"
						@reveal="showCategoryReveal"
					/>
				</article>
			</section>

			<section v-else-if="screen === 'categoryReveal'" class="category-reveal-overlay" role="dialog" aria-modal="true" :aria-labelledby="'category-reveal-title'">
				<article class="player-card category-reveal-modal">
					<span class="category-pill" :style="categoryStyle(selectedCategory)">{{ selectedCategory }}</span>
					<p class="category-reveal-label">{{ t('yourCategory') }}…</p>
					<h1 id="category-reveal-title">{{ selectedCategory }}</h1>
					<button type="button" class="primary-action wide-action" autofocus @click="openSelectedCategoryQuestion">{{ t('answerQuestion') }}</button>
				</article>
			</section>

			<section v-else-if="screen === 'clashBoard'" class="screen-shell mode-board-screen">
				<article class="mode-board-shell category-stage">
					<div class="clash-board-heading">
						<div class="mode-board-header"><h1>{{ game?.settings.categoryClashBoardTitle }}</h1><p class="muted">{{ game?.settings.categoryClashQuestionsCompletedLabel }}: {{ answers.length }} / 20</p></div>
						<div class="clash-score"><span>{{ game?.settings.categoryClashScoreLabel }}</span><strong>{{ score }}</strong></div>
					</div>
					<div class="clash-board">
						<div v-for="category in clashCategories" :key="category" class="clash-category">{{ category }}</div>
						<template v-for="value in [100, 200, 300, 400, 500]" :key="value">
							<button
								v-for="category in clashCategories"
								:key="`${category}-${value}`"
								type="button"
								class="clash-tile"
								:class="{
									used: usedQuestionIds.has(clashQuestions.find((question) => question.category === category && question.points === value)?.id ?? -1),
									correct: answers.find((answer) => answer.questionId === clashQuestions.find((question) => question.category === category && question.points === value)?.id)?.correct,
									incorrect: answers.find((answer) => answer.questionId === clashQuestions.find((question) => question.category === category && question.points === value)?.id)?.correct === false,
								}"
								:disabled="usedQuestionIds.has(clashQuestions.find((question) => question.category === category && question.points === value)?.id ?? -1)"
								@click="openClashQuestion(clashQuestions.find((question) => question.category === category && question.points === value))"
							>
								{{
									answers.find((answer) => answer.questionId === clashQuestions.find((question) => question.category === category && question.points === value)?.id)?.correct === false
										? '×'
										: usedQuestionIds.has(clashQuestions.find((question) => question.category === category && question.points === value)?.id ?? -1) ? '✓' : value
								}}
							</button>
						</template>
					</div>
					<div class="clash-submit-row">
						<button type="button" class="primary-action" :disabled="busy || answers.length < 1" @click="finishClashEarly">{{ game?.settings.categoryClashButtonLabel }}</button>
					</div>
				</article>
			</section>

			<section v-else-if="screen === 'bingoBoard'" class="screen-shell mode-board-screen">
				<article class="mode-board-shell bingo-status">
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<div class="mode-board-header">
						<h1 v-if="game?.settings.bingoTitle">{{ game.settings.bingoTitle }}</h1>
						<p v-if="game?.settings.bingoDescription" class="muted">{{ game.settings.bingoDescription }}</p>
					</div>
					<div class="mode-stats" :style="bingoBoardSize ? { maxWidth: `${bingoBoardSize}px` } : undefined">
						<div class="mode-stat"><span>{{ game?.settings.bingoAnsweredLabel }}</span><strong>{{ answers.length }} / 9</strong></div>
						<div class="mode-stat"><span>{{ game?.settings.bingoCorrectLabel }}</span><strong>{{ bingoCorrectCount }}</strong></div>
						<div class="mode-stat"><span>{{ game?.settings.bingoScoreLabel }}</span><strong>{{ score }}</strong></div>
						<div v-if="bingoRules.bonusEnabled" class="mode-stat bingo-bonus-stat"><span>{{ game?.settings.bingoBonusLabel }}</span><strong>{{ bingoScoreDetails.bonusPoints }}</strong></div>
					</div>
					<div ref="bingoBoardRegion" class="bingo-board-region">
						<div class="bingo-board" :style="bingoBoardSize ? { width: `${bingoBoardSize}px`, height: `${bingoBoardSize}px` } : undefined">
							<button
								v-for="(question, index) in bingoQuestions"
								:key="question.id"
								type="button"
								class="bingo-tile"
								:class="{ attempted: usedQuestionIds.has(question.id), marked: bingoMarked.includes(index) }"
								:disabled="usedQuestionIds.has(question.id)"
								:title="question.text"
								@click="openBingoQuestion(question, index)"
							>
								<span>{{ question.text }}</span>
							</button>
							<i
								v-for="line in bingoCompletedLines"
								:key="line.join('-')"
								class="bingo-line"
								:class="bingoLineClass(line)"
								aria-hidden="true"
							/>
						</div>
					</div>
					<div class="bingo-submit-row" :style="bingoBoardSize ? { maxWidth: `${bingoBoardSize}px` } : undefined">
						<button type="button" class="primary-action" :disabled="busy || !canSubmitBingo" @click="submitBingoBoard">{{ game?.settings.bingoSubmitButtonLabel }}</button>
						<p v-if="bingoRules.bonusEnabled" class="muted bingo-bonus-rules bingo-bonus-rules-desktop"><strong>{{ game?.settings.bingoBonusPointsLabel }}</strong><span><strong>+{{ bingoRules.patternBonusPoints }}</strong> {{ game?.settings.bingoBonusPointsDescription }}</span><span v-if="bingoRules.fullBoardBonusEnabled"><strong>+{{ bingoRules.fullBoardBonusPoints }}</strong> {{ t('fullBoardBonus') }}</span><span v-if="bingoRules.streakEnabled">{{ t('bingoStreakBonusRule') }}</span></p>
						<details v-if="bingoRules.bonusEnabled" class="muted bingo-bonus-rules-mobile">
							<summary>{{ t('bonusRules') }}</summary>
							<div class="bingo-bonus-rules-mobile-content">
								<span><strong>+{{ bingoRules.patternBonusPoints }}</strong> {{ game?.settings.bingoBonusPointsDescription }}</span>
								<span v-if="bingoRules.fullBoardBonusEnabled"><strong>+{{ bingoRules.fullBoardBonusPoints }}</strong> {{ t('fullBoardBonus') }}</span>
								<span v-if="bingoRules.streakEnabled">{{ t('bingoStreakBonusRule') }}</span>
							</div>
						</details>
					</div>
				</article>
			</section>

			<section v-else-if="screen === 'question' && currentQuestion" class="screen-shell">
				<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
				<QuestionView
					:question="currentQuestion"
					:question-number="currentQuestionNumber"
					:question-total="questionTotal"
					:selected="selected"
					:rank-order="rankOrder"
					:guess="guess"
					:guess-attempts-remaining="Math.max(0, 3 - guessHistory.length)"
					:guess-retry="guessRetry"
					:remaining-ms="remainingMs"
					:duration-ms="durationMs"
					:busy="busy"
					:feedback="feedback"
					:show-correct-responses="previewMode && ['correct', 'incorrect'].includes(previewAnswerState) ? true : (game?.settings.showCorrectResponses ?? false)"
					:show-streak="Boolean(game?.settings.scoring.streakEnabled && feedback?.correct && streakMultiplier > 1)"
					:streak-multiplier="streakMultiplier"
					:t="t"
					@update:selected="selected = $event"
					@update:rank-order="rankOrder = $event"
					@update:guess="guess = $event"
					@submit="submitAnswer(false)"
					@continue="continueAfterAnswer"
					@video-ready="beginQuestionTimer"
				/>
			</section>

			<section v-else-if="screen === 'roundPrompt'" class="screen-shell screen-narrow">
				<article class="player-card hero-card">
					<h1>{{ answers.length }} / 20</h1>
					<p>{{ score }} {{ t('points') }}</p>
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<div class="action-row confirmation-actions">
						<button type="button" class="primary-action" @click="screen = 'clashBoard'">{{ t('continueRound') }}</button>
						<button type="button" class="secondary-action" @click="finishClashEarly">{{ t('endGame') }}</button>
					</div>
				</article>
			</section>

			<section v-else-if="screen === 'final' && result" class="screen-shell final-screen-shell" :class="{ 'has-full-leaderboard': game?.settings.completion.showLeaderboard }">
				<div class="final-left-column">
					<article class="player-card hero-card final-card final-message-card">
						<div class="feedback-icon feedback-correct" aria-hidden="true">✓</div>
						<h1>{{ game?.settings.completion.title }}</h1>
						<p>{{ game?.settings.completion.message || game?.gameOverText }}</p>
					</article>
					<section class="player-card final-card final-summary-card">
						<div class="summary-grid" :class="{ 'summary-grid-two': !game?.settings.completion.showScore }">
							<div v-if="game?.settings.completion.showScore" class="summary-item"><span>{{ t('finalScore') }}</span><strong>{{ result.finalScore }}</strong></div>
							<div class="summary-item"><span>{{ t('correctAnswers') }}</span><strong>{{ result.correctAnswers }} / {{ result.questionsAnswered }}</strong></div>
							<div class="summary-item"><span>{{ t('completionTime') }}</span><strong>{{ (result.completionTimeMs / 1000).toFixed(1) }}s</strong></div>
						</div>
					</section>
					<section v-if="showFinalActions" class="player-card final-card final-actions-card">
						<div class="action-row final-action-row">
							<button v-if="customCallToActionEnabled" type="button" class="primary-action" @click="primaryAction">{{ game?.settings.completion.primaryLabel || t('continue') }}</button>
							<button v-if="canReplay" type="button" :class="customCallToActionEnabled ? 'secondary-action' : 'primary-action'" @click="playAgain">{{ t('playAgain') }}</button>
							<button v-if="kiosk" type="button" :class="canReplay ? 'secondary-action' : 'primary-action'" @click="done">{{ endGameButtonLabel }}</button>
						</div>
					</section>
				</div>
				<article v-if="game?.settings.completion.showLeaderboard" class="player-card leaderboard-card final-full-leaderboard">
					<h1>{{ t('leaderboard') }}</h1>
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<div class="table-scroll">
						<table class="leaderboard-table">
							<thead><tr><th>{{ t('rank') }}</th><th>{{ t('player') }}</th><th>{{ t('score') }}</th></tr></thead>
							<tbody>
								<tr v-for="row in leaderboard" :key="`${row.playerId}-${row.rank}`" :class="{ you: row.playerId === player?.id }">
									<td>{{ row.rank }}</td><td>{{ row.name }}</td><td>{{ row.score }}</td>
								</tr>
								<tr v-if="!leaderboard.length"><td colspan="3" class="muted">{{ t('noResults') }}</td></tr>
							</tbody>
						</table>
					</div>
				</article>
			</section>

			<section v-else-if="screen === 'leaderboard'" class="screen-shell full-leaderboard-screen" :class="{ 'has-final-result': result }">
				<article v-if="result" class="player-card hero-card final-card full-leaderboard-final-card">
					<div class="feedback-icon feedback-correct" aria-hidden="true">✓</div>
					<h1>{{ game?.settings.completion.title }}</h1>
					<p>{{ game?.settings.completion.message || game?.gameOverText }}</p>
					<div class="summary-grid" :class="{ 'summary-grid-two': !game?.settings.completion.showScore }">
						<div v-if="game?.settings.completion.showScore" class="summary-item"><span>{{ t('finalScore') }}</span><strong>{{ result.finalScore }}</strong></div>
						<div class="summary-item"><span>{{ t('correctAnswers') }}</span><strong>{{ result.correctAnswers }} / {{ result.questionsAnswered }}</strong></div>
						<div class="summary-item"><span>{{ t('completionTime') }}</span><strong>{{ (result.completionTimeMs / 1000).toFixed(1) }}s</strong></div>
					</div>
				</article>
				<article class="player-card leaderboard-card">
					<h1>{{ t('leaderboard') }}</h1>
					<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
					<div class="table-scroll">
						<table class="leaderboard-table">
							<thead><tr><th>{{ t('rank') }}</th><th>{{ t('player') }}</th><th>{{ t('score') }}</th></tr></thead>
							<tbody>
								<tr v-for="row in leaderboard" :key="`${row.playerId}-${row.rank}`" :class="{ you: row.playerId === player?.id }">
									<td>{{ row.rank }}</td><td>{{ row.name }}</td><td>{{ row.score }}</td>
								</tr>
								<tr v-if="!leaderboard.length"><td colspan="3" class="muted">{{ t('noResults') }}</td></tr>
							</tbody>
						</table>
					</div>
					<div v-if="player" class="action-row">
						<button type="button" class="secondary-action" @click="screen = result ? 'final' : 'instructions'">{{ t('back') }}</button>
						<button v-if="kiosk" type="button" class="primary-action" @click="resetToLogin">{{ t('home') }}</button>
					</div>
				</article>
			</section>
		</main>

		<div v-if="rewardPopup" class="reward-popup-overlay" @keydown.esc="rewardPopup = null">
			<article
				class="player-card reward-popup-card"
				role="dialog"
				aria-modal="true"
				aria-labelledby="reward-popup-title"
			>
				<img v-if="rewardPopup.popup.imageUrl" class="reward-popup-image" :src="rewardPopup.popup.imageUrl" alt="">
				<div v-else class="reward-popup-icon" aria-hidden="true"><span class="fa fa-gift" /></div>
				<h2 id="reward-popup-title">{{ rewardPopup.popup.title }}</h2>
				<p class="reward-popup-message">{{ rewardPopup.popup.message }}</p>
				<div v-if="rewardPopup.prize && (rewardPopup.prize.name || rewardPopup.prize.description)" class="reward-popup-prize">
					<strong v-if="rewardPopup.prize.name">{{ rewardPopup.prize.name }}</strong>
					<span v-if="rewardPopup.prize.description">{{ rewardPopup.prize.description }}</span>
				</div>
				<button type="button" class="primary-action wide-action" autofocus @click="rewardPopup = null">{{ rewardPopup.popup.buttonLabel }}</button>
			</article>
		</div>

		<div v-if="playerConfirmation" class="player-confirmation-overlay" @keydown.esc="closePlayerConfirmation">
			<article class="player-card player-confirmation-card" role="dialog" aria-modal="true" aria-labelledby="player-confirmation-title">
				<h2 id="player-confirmation-title">{{ playerConfirmationTitle }}</h2>
				<div v-if="errorMessage" class="error-banner" role="alert">{{ errorMessage }}</div>
				<div v-if="playerConfirmation === 'submitBingo'" class="muted">
					<p>{{ t('finalScore') }}: <strong>{{ bingoScoreDetails.total }}</strong></p>
					<p>{{ t('bonusEarned') }} <strong>{{ bingoScoreDetails.bonusPoints }}</strong></p>
				</div>
				<div class="confirmation-actions">
					<button ref="confirmationCancelButton" type="button" class="secondary-action" :disabled="busy" @click="closePlayerConfirmation">{{ playerConfirmationCancelLabel }}</button>
					<button type="button" class="primary-action" :disabled="busy" @click="confirmPlayerAction"><span v-if="busy" class="button-spinner" aria-hidden="true" />{{ playerConfirmationActionLabel }}</button>
				</div>
			</article>
		</div>

		<div v-if="activeBingoBonus" class="player-confirmation-overlay bingo-bonus-overlay">
			<article class="player-card player-confirmation-card bingo-bonus-card" role="dialog" aria-modal="true" aria-labelledby="bingo-bonus-title">
				<h2 id="bingo-bonus-title">{{ game?.settings.scoring.bingoBonusTitle }}</h2>
				<div class="bingo-bonus-points"><strong>+{{ activeBingoBonus.points }}</strong><span>{{ game?.settings.bingoBonusLabel }}</span></div>
				<p class="muted bingo-bonus-copy">{{ activeBingoBonus.copy }}</p>
				<button type="button" class="primary-action wide-action" autofocus @click="showNextBingoBonus">{{ t('continue') }}</button>
			</article>
		</div>

	</div>
</template>
